<template>
  <div class="account" v-if="account">
    <v-layout column align-center justify-center>
      <v-card
        elevation="4"
        min-width="320"
        max-width="600"
        class="mx-auto mt-12 mb-5 px-10"
        ref="form"
      >
        <v-card-title>{{ account.name || "New Account" }}</v-card-title>
        <v-card-subtitle>
          <v-row dense v-if="!account_creation">
            <v-col>
              {{ account.account_id }}
              <span v-if="account.active">&nbsp;&nbsp;(Active)</span>
              <span v-else>&nbsp;&nbsp;(Inactive)</span>
            </v-col>

            <account-actions
              :account="account"
              show_users
              show_usage
              show_orders
            ></account-actions>
          </v-row>
        </v-card-subtitle>
        <v-card-text>
          <hr />
          <br />
          <v-row
            ><v-layout justify-center>
              <v-btn
                v-if="
                  read_only &
                  !disable_edit &
                  account.active &
                  user_details.super_admin
                "
                class="ma-4 button_text--text"
                elevation="4"
                color="primary"
                @click="deleteAccount()"
              >
                Disable Account
              </v-btn>
              <v-btn
                v-if="read_only && !account.active && user_details.super_admin"
                class="ma-4 button_text--text"
                elevation="4"
                color="primary"
                @click="enableAccount()"
              >
                Enable Account
              </v-btn>
              <v-btn
                v-if="read_only && !disable_edit && user_details.super_admin"
                class="ma-4 button_text--text"
                elevation="4"
                color="primary"
                @click="flipReadOnly()"
              >
                Edit Account
              </v-btn>
            </v-layout></v-row
          >
          <v-row>
            <v-layout justify-center>
              <v-btn
                v-if="
                  !account_details.use_latest &
                  user_details.admin &
                  (user_details.account_id === account.account_id)
                "
                class="ma-4"
                elevation="4"
                outlined
                color="#E5E1DA"
                @click="
                  $router.push({
                    name: 'upgrade-to-latest',
                  })
                "
              >
                <v-icon left> mdi-transfer-up </v-icon>
                Upgrade to latest ARD version
              </v-btn>
            </v-layout>
          </v-row>

          <!-- Read Only View -->
          <v-row v-if="read_only">
            <!-- Account info -->
            <v-col>
              <v-row>
                <v-col>
                  Info

                  <div v-bind:style="{ fontSize: '14px' }" class="mx-4">
                    Account ID: {{ account.account_id }} <br />
                    Account Name: {{ account.name }} <br />
                    Internal Account: {{ account.internal }} <br />
                    <span v-show="!is_internal">
                      SAP: {{ account.sap }} <br
                    /></span>
                    <span v-show="is_internal">
                      Cost Center: {{ account.cost_center }} <br
                    /></span>
                    Contract Start: {{ account.contract_start }} <br />
                    Contract End: {{ account.contract_end }} <br />
                    <span v-if="user_details.super_admin">
                      Sales Support Contacts:
                      {{ get_sales_support_contacts() }}
                    </span>
                  </div>

                  Pricing

                  <div v-bind:style="{ fontSize: '14px' }" class="mx-4">
                    Fresh Archive Imagery Price:
                    {{
                      format_limits_prices(account.pricing.fresh_imagery_price)
                    }}
                    <br />
                    Standard Archive Imagery Price:
                    {{
                      format_limits_prices(
                        account.pricing.standard_imagery_price
                      )
                    }}
                    <br />
                    Training Archive Imagery Price:
                    {{
                      format_limits_prices(
                        account.pricing.training_imagery_price
                      )
                    }}
                    <br />
                    Tasking Imagery Price:
                    {{
                      format_limits_prices(
                        account.pricing.tasking_imagery_price
                      )
                    }}
                    <br />
                  </div>

                  Fee Limits

                  <div v-bind:style="{ fontSize: '14px' }" class="mx-4">
                    Annual Archive Subscription Fee Limit:
                    {{
                      format_limits_prices(
                        account.limits.annual_subscription_fee_limit
                      )
                    }}
                    <br />
                    Fresh Archive Imagery Fee Limit:
                    {{
                      format_limits_prices(
                        account.limits.fresh_imagery_fee_limit
                      )
                    }}
                    <br />
                    Standard Archive Imagery Fee Limit:
                    {{
                      format_limits_prices(
                        account.limits.standard_imagery_fee_limit
                      )
                    }}
                    <br />
                    Training Archive Imagery Fee Limit:
                    {{
                      format_limits_prices(
                        account.limits.training_imagery_fee_limit
                      )
                    }}
                    <br />
                    Tasking Imagery Fee Limit:
                    {{
                      format_limits_prices(
                        account.limits.tasking_imagery_fee_limit
                      )
                    }}
                    <br />
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- Edit View -->
          <v-row v-if="!read_only">
            <!-- Account info -->
            <v-col>
              Info
              <v-row no-gutters>
                <v-col>
                  <v-text-field
                    ref="name"
                    class="pt-5"
                    :readonly="read_only"
                    v-model="account.name"
                    :rules="[(v) => !!v || 'Name is required']"
                    label="Account Name"
                    outlined
                    dense
                    hint="Required"
                    persistent-hint
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-spacer></v-spacer>

              <v-row>
                <v-col>
                  <v-switch
                    :readonly="read_only"
                    v-model="account.internal"
                    label="Internal Account"
                  ></v-switch>
                  <v-text-field
                    ref="external"
                    v-show="!is_internal"
                    :rules="[
                      (v) => !!v || account.internal || 'SAP is required',
                    ]"
                    :readonly="read_only"
                    :persistent-placeholder="true"
                    hint="Required"
                    persistent-hint
                    placeholder="None"
                    v-model="account.sap"
                    label="SAP"
                    outlined
                    dense
                  ></v-text-field>
                  <v-text-field
                    ref="internal"
                    v-show="is_internal"
                    :rules="[
                      (v) =>
                        !!v || !account.internal || 'Cost Center is required',
                    ]"
                    :readonly="read_only"
                    :persistent-placeholder="true"
                    hint="Required"
                    persistent-hint
                    placeholder="None"
                    v-model="account.cost_center"
                    label="Cost Center"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <date-picker
                    @change="account.contract_start = $event"
                    :initialValue="account.contract_start"
                    :show_hint="true"
                    :readonly="read_only"
                    label="Contract Start"
                    textColor="primary"
                    dateColor="primary"
                    dateBtnColor="primary"
                    :validate="true"
                  ></date-picker>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <date-picker
                    @change="account.contract_end = $event"
                    :initialValue="account.contract_end"
                    :show_hint="true"
                    :readonly="read_only"
                    label="Contract End"
                    textColor="primary"
                    dateColor="primary"
                    dateBtnColor="primary"
                    :validate="true"
                  ></date-picker>
                </v-col>
              </v-row>

              <v-row no-gutters>
                <v-col>
                  Sales Support Contacts
                  <v-row class="pt-3">
                    <v-text-field
                      :readonly="read_only"
                      v-model="sales_support_field"
                      label="Sales Support Contacts"
                      placeholder="example_contact@maxar.com"
                      :persistent-placeholder="true"
                      outlined
                      dense
                      hint="Type the email, then press enter or click the button"
                      persistent-hint
                      @keydown.enter="
                        addSalesSupportContact(sales_support_field)
                      "
                      class="pt-3"
                    ></v-text-field>
                    <v-btn
                      @click="addSalesSupportContact(sales_support_field)"
                      class="mt-3 mx-2"
                      small
                      fab
                    >
                      <v-icon color="primary"> mdi-plus </v-icon>
                    </v-btn>
                  </v-row>
                  <ul>
                    <li
                      v-for="item in account.sales_support_contacts"
                      :key="item"
                    >
                      {{ item }}
                      <v-icon
                        right
                        size="20"
                        @click="removeSalesSupportContact(item)"
                      >
                        mdi-alpha-x
                      </v-icon>
                    </li>
                  </ul>
                </v-col>
              </v-row>
              <br />

              <!-- Pricing -->
              Pricing

              <v-row>
                <v-col>
                  <v-text-field
                    class="pt-5"
                    :readonly="read_only"
                    :persistent-placeholder="true"
                    placeholder="None"
                    type="number"
                    :value="account.pricing.fresh_imagery_price"
                    @input="
                      account.pricing.fresh_imagery_price =
                        $event !== '' ? Number($event) : null
                    "
                    label="Fresh Archive Imagery Price"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field
                    :readonly="read_only"
                    :persistent-placeholder="true"
                    placeholder="None"
                    type="number"
                    :value="account.pricing.standard_imagery_price"
                    @input="
                      account.pricing.standard_imagery_price =
                        $event !== '' ? Number($event) : null
                    "
                    label="Standard Archive Imagery Price"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field
                    :readonly="read_only"
                    :persistent-placeholder="true"
                    placeholder="None"
                    type="number"
                    :value="account.pricing.training_imagery_price"
                    @input="
                      account.pricing.training_imagery_price =
                        $event !== '' ? Number($event) : null
                    "
                    label="Training Archive Imagery Price"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field
                    :readonly="read_only"
                    :persistent-placeholder="true"
                    placeholder="None"
                    type="number"
                    :value="account.pricing.tasking_imagery_price"
                    @input="
                      account.pricing.tasking_imagery_price =
                        $event !== '' ? Number($event) : null
                    "
                    label="Tasking Imagery Price"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Limits -->
              Fee Limits
              <v-row>
                <v-col>
                  <v-text-field
                    v-show="is_internal"
                    class="pt-5"
                    :readonly="read_only"
                    :persistent-placeholder="true"
                    placeholder="None"
                    type="number"
                    :value="account.limits.annual_subscription_fee_limit"
                    @input="
                      account.limits.annual_subscription_fee_limit =
                        $event !== '' ? Number($event) : null
                    "
                    label="Annual Archive Subscription Fee Limit"
                    outlined
                    dense
                  ></v-text-field>
                  <v-text-field
                    v-show="!is_internal"
                    ref="annual_limit"
                    hint="Required"
                    persistent-hint
                    :rules="[
                      (v) =>
                        !!v ||
                        v === 0 ||
                        'Annual Subscription Fee Limit is required',
                    ]"
                    class="pt-5"
                    :readonly="read_only"
                    :persistent-placeholder="true"
                    placeholder="None"
                    type="number"
                    :value="account.limits.annual_subscription_fee_limit"
                    @input="
                      account.limits.annual_subscription_fee_limit =
                        $event !== '' ? Number($event) : null
                    "
                    label="Annual Archive Subscription Fee Limit"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field
                    :readonly="read_only"
                    :persistent-placeholder="true"
                    placeholder="None"
                    type="number"
                    :value="account.limits.fresh_imagery_fee_limit"
                    @input="
                      account.limits.fresh_imagery_fee_limit =
                        $event !== '' ? Number($event) : null
                    "
                    label="Fresh Archive Imagery Fee Limit"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field
                    :readonly="read_only"
                    :persistent-placeholder="true"
                    placeholder="None"
                    type="number"
                    :value="account.limits.standard_imagery_fee_limit"
                    @input="
                      account.limits.standard_imagery_fee_limit =
                        $event !== '' ? Number($event) : null
                    "
                    label="Standard Archive Imagery Fee Limit"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field
                    :readonly="read_only"
                    :persistent-placeholder="true"
                    placeholder="None"
                    type="number"
                    :value="account.limits.training_imagery_fee_limit"
                    @input="
                      account.limits.training_imagery_fee_limit =
                        $event !== '' ? Number($event) : null
                    "
                    label="Training Archive Imagery Fee Limit"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field
                    :readonly="read_only"
                    :persistent-placeholder="true"
                    placeholder="None"
                    type="number"
                    v-model.number="account.limits.tasking_imagery_fee_limit"
                    label="Tasking Imagery Fee Limit"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row v-if="!read_only"
            ><v-layout justify-center>
              <v-btn
                v-if="!account_creation && user_details.super_admin"
                class="ma-4"
                elevation="4"
                outlined
                color="#E5E1DA"
                @click="flipReadOnly()"
              >
                Cancel
              </v-btn>
              <v-btn
                v-if="account_creation && user_details.super_admin"
                class="ma-4"
                elevation="4"
                outlined
                color="#E5E1DA"
                @click="
                  $router.push({
                    name: 'account-landing',
                  })
                "
              >
                Cancel
              </v-btn>
              <v-btn
                v-if="!account_creation & user_details.super_admin"
                class="ma-4 button_text--text"
                elevation="4"
                color="primary"
                @click="updateAccount(account)"
              >
                Save Account
              </v-btn>
              <v-btn
                v-if="account_creation && user_details.super_admin"
                class="ma-4 button_text--text"
                elevation="4"
                color="primary"
                @click="createAccount(account)"
              >
                Create Account
              </v-btn>
            </v-layout></v-row
          >
        </v-card-text>
      </v-card>

      <div class="float-right caption" v-if="account.created">
        <span
          >Account created:
          {{ new Date(account.created).toLocaleString() }}</span
        >
        &nbsp;&nbsp;&nbsp;&nbsp; &nbsp;&nbsp;&nbsp;&nbsp;
        <span
          >Last modified:
          {{ new Date(account.modified).toLocaleString() }}</span
        >
      </div>
    </v-layout>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import DatePicker from "@/components/shared/DatePicker.vue";
import AccountActions from "@/components/account/AccountActions.vue";

export default {
  props: ["account", "read_only", "account_creation", "disable_edit"],
  components: {
    DatePicker,
    AccountActions,
  },
  data() {
    return {
      usage_range_start: null,
      usage_range_end: null,
      requiredFields: ["name", "external", "internal", "annual_limit"],
      sales_support_field: "",
    };
  },
  computed: {
    ...mapGetters(["user_details", "account_details"]),

    is_internal() {
      if (this.account) {
        return this.account.internal;
      }
      return false;
    },
  },
  watch: {
    is_internal() {
      this.$forceUpdate();
    },
  },
  methods: {
    flipReadOnly() {
      this.$emit("read_only_changed");
    },
    updateAccount(account) {
      if (!account.sales_support_contacts) {
        delete account.sales_support_contacts;
      }
      this.$emit("update_account", { account: account });
    },
    deleteAccount() {
      this.$emit("delete_account");
    },
    enableAccount() {
      this.$emit("enable_account");
    },
    createAccount(account) {
      let valid = true;
      if (account.internal) {
        this.requiredFields = this.requiredFields.filter(
          (item) => item !== "annual_limit"
        );
        delete account.sap;
      }
      this.requiredFields.forEach((f) => {
        if (!this.$refs[f].validate(true)) {
          valid = false;
        }
      });

      if (valid) {
        this.$emit("create_account", { account: account });
      }
    },
    format_limits_prices(value) {
      return value || value === 0 ? value : "None";
    },
    removeSalesSupportContact(email) {
      this.account.sales_support_contacts.splice(
        this.account.sales_support_contacts.indexOf(email),
        1
      );
    },
    addSalesSupportContact(email) {
      if (email) {
        if (!this.account.sales_support_contacts) {
          this.account.sales_support_contacts = [];
          this.account.sales_support_contacts.push(email);
        } else {
          this.account.sales_support_contacts.push(email);
        }
      }
      this.sales_support_field = "";
    },
    get_sales_support_contacts() {
      if (this.account.sales_support_contacts) {
        return this.account.sales_support_contacts.join(", ");
      } else {
        return "";
      }
    },
  },
};
</script>
<style lang="scss">
.v-application .error--text {
  color: #df323b !important;
}
</style>
